import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { adminExtAPI, emailAPI } from '../lib/api';
import { Settings, Key, Mail, CreditCard, Globe, HelpCircle, Save, Loader2, CheckCircle, XCircle, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const HELP_TEXT = {
  stripe: {
    title: 'Stripe Setup',
    steps: '1. Go to stripe.com and create an account\n2. Navigate to Developers > API Keys\n3. Copy your Publishable Key (pk_...) and Secret Key (sk_...)\n4. For testing, use Test mode keys\n5. Enter both keys below and save',
  },
  email: {
    title: 'Email Service Setup',
    steps: '1. Choose a provider: SendGrid, Resend, or SMTP\n2. For SendGrid: Go to sendgrid.com > Settings > API Keys\n3. For Resend: Go to resend.com > API Keys\n4. For SMTP: Use your email provider\'s SMTP settings (e.g., smtp.gmail.com:587)\n5. Enter credentials below and save',
  },
  mypos: {
    title: 'myPOS Setup',
    steps: '1. Log in to your myPOS developer portal\n2. Go to Applications > Your App\n3. Copy your Client ID and Client Secret\n4. Enter them below and save',
  },
  namesilo: {
    title: 'NameSilo Setup',
    steps: '1. Log in to namesilo.com\n2. Go to Account > API Manager\n3. Generate or copy your API Key\n4. Enter the key below and save',
  },
  godaddy: {
    title: 'GoDaddy Setup',
    steps: '1. Go to developer.godaddy.com\n2. Create an API Key under My Account\n3. Copy both the API Key and Secret\n4. Note: Appraisals require an upgraded plan\n5. Enter them below and save',
  },
  analytics: {
    title: 'Google Analytics Setup',
    steps: '1. Go to analytics.google.com\n2. Create a new GA4 property\n3. Go to Admin > Data Streams > Web\n4. Copy your Measurement ID (G-XXXXXXXXXX)\n5. Enter the ID below and enable tracking',
  },
  oauth: {
    title: 'OAuth Providers Setup',
    steps: 'Configure social login providers. Google login is powered by Emergent Auth (works immediately).\n\nFor other providers:\n1. Apple: Create an App ID at developer.apple.com > Certificates, Identifiers & Profiles\n2. Facebook: Create an app at developers.facebook.com > My Apps\n3. GitHub: Go to github.com > Settings > Developer Settings > OAuth Apps\n4. Enter Client ID and Secret for each provider you want to enable',
  },
};

function SettingCard({ name, config, onSave }) {
  const [form, setForm] = useState(config || {});
  const [saving, setSaving] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const help = HELP_TEXT[name];

  const fields = {
    stripe: [
      { key: 'publishable_key', label: 'Publishable Key', placeholder: 'pk_test_...' },
      { key: 'secret_key', label: 'Secret Key', placeholder: 'sk_test_...', sensitive: true },
    ],
    email: [
      { key: 'provider', label: 'Provider', placeholder: 'sendgrid / resend / smtp' },
      { key: 'api_key', label: 'API Key', placeholder: 'SG.xxxx or re_xxxx', sensitive: true },
      { key: 'smtp_host', label: 'SMTP Host (if SMTP)', placeholder: 'smtp.gmail.com' },
      { key: 'smtp_port', label: 'SMTP Port', placeholder: '587' },
      { key: 'from_email', label: 'From Email', placeholder: 'noreply@xdomainx.com' },
    ],
    mypos: [
      { key: 'client_id', label: 'Client ID', placeholder: 'Your myPOS Client ID' },
      { key: 'client_secret', label: 'Client Secret', placeholder: 'Your myPOS Secret', sensitive: true },
    ],
    namesilo: [
      { key: 'api_key', label: 'API Key', placeholder: 'Your NameSilo API Key', sensitive: true },
    ],
    godaddy: [
      { key: 'api_key', label: 'API Key', placeholder: 'Your GoDaddy API Key' },
      { key: 'api_secret', label: 'API Secret', placeholder: 'Your GoDaddy Secret', sensitive: true },
      { key: 'enabled', label: 'Enable GoDaddy API', placeholder: 'true / false', type: 'toggle' },
    ],
    analytics: [
      { key: 'tracking_id', label: 'Measurement ID', placeholder: 'G-XXXXXXXXXX' },
      { key: 'enabled', label: 'Enable Tracking', placeholder: 'true / false', type: 'toggle' },
    ],
    oauth: [
      { key: 'google_enabled', label: 'Google Login (Emergent Auth)', type: 'toggle' },
      { key: 'apple_client_id', label: 'Apple Client ID', placeholder: 'com.yourapp.service' },
      { key: 'apple_client_secret', label: 'Apple Client Secret', placeholder: 'Apple Secret', sensitive: true },
      { key: 'apple_enabled', label: 'Enable Apple Login', type: 'toggle' },
      { key: 'facebook_client_id', label: 'Facebook App ID', placeholder: 'Your Facebook App ID' },
      { key: 'facebook_client_secret', label: 'Facebook App Secret', placeholder: 'Facebook Secret', sensitive: true },
      { key: 'facebook_enabled', label: 'Enable Facebook Login', type: 'toggle' },
      { key: 'github_client_id', label: 'GitHub Client ID', placeholder: 'Your GitHub Client ID' },
      { key: 'github_client_secret', label: 'GitHub Client Secret', placeholder: 'GitHub Secret', sensitive: true },
      { key: 'github_enabled', label: 'Enable GitHub Login', type: 'toggle' },
    ],
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(name, form);
      toast.success(`${name} settings saved`);
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  return (
    <div className="p-6 bg-[#0A0A0A] border border-white/5 rounded-sm" data-testid={`settings-${name}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-white font-['Outfit'] capitalize">{name}</h3>
        <button onClick={() => setShowHelp(!showHelp)} className="text-zinc-500 hover:text-[#00E5FF] transition-colors" data-testid={`help-${name}`}>
          <HelpCircle className="h-5 w-5" />
        </button>
      </div>
      {showHelp && (
        <div className="mb-4 p-4 bg-[#050505] border border-[#00E5FF]/20 rounded-sm text-sm">
          <h4 className="text-[#00E5FF] font-bold mb-2">{help?.title}</h4>
          <pre className="text-zinc-400 whitespace-pre-wrap text-xs">{help?.steps}</pre>
        </div>
      )}
      <div className="space-y-3">
        {(fields[name] || []).map(f => (
          <div key={f.key}>
            <Label className="text-zinc-500 text-[10px] uppercase tracking-wider">{f.label}</Label>
            {f.type === 'toggle' ? (
              <div className="mt-2">
                <label className="flex items-center gap-3 cursor-pointer">
                  <div className={`relative w-11 h-6 rounded-full transition-colors ${form[f.key] === true || form[f.key] === 'true' ? 'bg-[#00E5FF]' : 'bg-zinc-700'}`}
                    onClick={() => {
                      const current = form[f.key] === true || form[f.key] === 'true';
                      setForm({ ...form, [f.key]: !current });
                    }}>
                    <div className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${form[f.key] === true || form[f.key] === 'true' ? 'translate-x-5' : ''}`} />
                  </div>
                  <span className={`text-sm ${form[f.key] === true || form[f.key] === 'true' ? 'text-[#00E5FF]' : 'text-zinc-500'}`}>
                    {form[f.key] === true || form[f.key] === 'true' ? 'Enabled' : 'Disabled'}
                  </span>
                </label>
              </div>
            ) : (
              <Input type={f.sensitive ? 'password' : 'text'} value={form[f.key] || ''} onChange={e => setForm({ ...form, [f.key]: e.target.value })}
                placeholder={f.placeholder} className="mt-1 bg-[#050505] border-zinc-800 text-white rounded-sm" data-testid={`setting-${name}-${f.key}`} />
            )}
          </div>
        ))}
      </div>
      <Button onClick={handleSave} disabled={saving} className="mt-4 bg-[#00E5FF] text-black font-bold rounded-sm" data-testid={`save-${name}`}>
        {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />} Save
      </Button>
    </div>
  );
}

export default function AdminSettingsPage() {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [emailStatus, setEmailStatus] = useState(null);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);

  useEffect(() => {
    Promise.all([
      adminExtAPI.settings(),
      emailAPI.status()
    ]).then(([settingsRes, emailRes]) => {
      setSettings(settingsRes.data.settings || {});
      setEmailStatus(emailRes.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const handleSave = async (key, data) => {
    await adminExtAPI.updateSettings(key, data);
    const res = await adminExtAPI.settings();
    setSettings(res.data.settings || {});
  };

  const handleSendTestEmail = async () => {
    if (!testEmail) { toast.error('Please enter an email address'); return; }
    setSendingTest(true);
    try {
      await emailAPI.sendTest({ to_email: testEmail });
      toast.success(`Test email sent to ${testEmail}`);
      const res = await emailAPI.status();
      setEmailStatus(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send test email');
    }
    setSendingTest(false);
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#00E5FF]" /></div>;

  return (
    <div className="px-6 md:px-12 max-w-5xl mx-auto py-12 md:py-20" data-testid="admin-settings-page">
      <div className="flex items-center gap-3 mb-8">
        <Settings className="h-6 w-6 text-[#00E5FF]" />
        <h1 className="text-4xl font-bold text-white font-['Outfit'] tracking-tight">API Settings</h1>
      </div>
      <p className="text-zinc-500 mb-8">Configure third-party service integrations. Click the <HelpCircle className="h-4 w-4 inline text-zinc-500" /> icon for setup instructions.</p>
      
      {/* Email Service Status Card */}
      {emailStatus && (
        <div className="mb-8 p-6 bg-[#0A0A0A] border border-white/5 rounded-sm" data-testid="email-status-card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Mail className="h-5 w-5 text-[#00E5FF]" />
              <h3 className="text-lg font-bold text-white">Email Service Status</h3>
            </div>
            {emailStatus.sendgrid_configured ? (
              <span className="flex items-center gap-2 px-3 py-1 bg-green-500/10 text-green-400 text-xs font-bold rounded-sm">
                <CheckCircle className="h-4 w-4" /> CONNECTED
              </span>
            ) : (
              <span className="flex items-center gap-2 px-3 py-1 bg-red-500/10 text-red-400 text-xs font-bold rounded-sm">
                <XCircle className="h-4 w-4" /> NOT CONFIGURED
              </span>
            )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="p-3 bg-[#050505] border border-white/5 rounded-sm">
              <div className="text-[10px] text-zinc-600 uppercase tracking-wider mb-1">Sender Email</div>
              <div className="text-sm text-white">{emailStatus.sender_email}</div>
            </div>
            <div className="p-3 bg-[#050505] border border-white/5 rounded-sm">
              <div className="text-[10px] text-zinc-600 uppercase tracking-wider mb-1">Emails Sent</div>
              <div className="text-sm text-[#00E5FF] font-bold">{emailStatus.stats?.total_sent || 0}</div>
            </div>
            <div className="p-3 bg-[#050505] border border-white/5 rounded-sm">
              <div className="text-[10px] text-zinc-600 uppercase tracking-wider mb-1">Failed</div>
              <div className="text-sm text-red-400 font-bold">{emailStatus.stats?.failed || 0}</div>
            </div>
          </div>
          
          {emailStatus.sendgrid_configured && (
            <div className="flex items-center gap-3">
              <Input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="Enter email to send test"
                className="flex-1 bg-[#050505] border-zinc-800 text-white rounded-sm"
                data-testid="test-email-input"
              />
              <Button onClick={handleSendTestEmail} disabled={sendingTest} className="bg-[#00E5FF] text-black font-bold rounded-sm" data-testid="send-test-email-btn">
                {sendingTest ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                Send Test
              </Button>
            </div>
          )}
        </div>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {['stripe', 'email', 'mypos', 'namesilo', 'godaddy', 'analytics', 'oauth'].map(key => (
          <SettingCard key={key} name={key} config={settings[key] || {}} onSave={handleSave} />
        ))}
      </div>
    </div>
  );
}
